<template>
	<div v-if="isCartDataReady">
		<EmptyCart v-if="numOfProducts === 0 && numOfSamples === 0" />
		<div
			v-else
			class="cart_container"
		>
			<div class="content_wrapper">
				<div class="content">
					<div class="title">
						<h2 class="tg-title-font tg-title-color">
							{{ $t("pages.cart.my_cart") }}
							<span class="quantity">
								{{ productsAndSamples }}
							</span>
						</h2>
						<div class="total_button">
							<div class="total">
								<span class="label">{{ $t("pages.cart.total") }}</span>
								<span class="total_price">$ -<span class="dollar">USD</span></span>
							</div>
							<Button
								color="primary"
								block
								class="mt-[10px]"
								@click="goToCheckout"
							>
								{{ $t("pages.cart.go_to_checkout") }}
								<Icon name="md-chevron_right" />
							</Button>
						</div>
					</div>
					<div class="alert_container">
						<Alert
							type="info"
							class="alert"
						>
							{{ $t("pages.cart.not_reserved") }}
							<span
								class="tg-link-color cursor-pointer"
								@click="goToCheckout"
							>
								{{ $t("pages.cart.place_order") }}
							</span>
						</Alert>
						<!-- <Alert
					type="warning"
					class="alert"
				>
					You must select a minimum of 10 different product samples. Please add 8 more samples.
					<span class="tg-link-color">Browse catalog</span>
				</Alert> -->
					</div>

					<div class="cart">
						<div class="order_cards">
							<OrderCardV3
								v-if="numOfProducts > 0 && numOfSamples > 0"
								:cart-products="cartProductsGroupByWarehouse"
								:num-of-products="numOfProducts"
								:cart-samples="cartSamples"
								:num-of-samples="numOfSamples"
							/>
							<OrderCardV2
								v-else-if="numOfProducts > 0 && numOfSamples === 0"
								:cart-products="cartProductsGroupByWarehouse"
								:num-of-products="numOfProducts"
							/>
							<OrderCardV2
								v-else
								:cart-samples="cartSamples"
								:num-of-samples="numOfSamples"
							/>
							<span
								class="continue_shopping tg-link-color"
								@click="goShopping"
							>
								<Icon name="md-chevron_left" />
								{{ $t("pages.cart.continue_shopping") }}
							</span>
						</div>
					</div>
				</div>
			</div>
			<OrderSummary
				:has-both="hasBoth"
				class="order_summary"
			/>
		</div>
	</div>
</template>
<script setup lang="ts">
import { useI18n } from "vue-i18n";
import type { Toast } from "@tengiva/components";
import { useGtag } from "vue-gtag-next";
import Alert from "@/components/Alert.vue";
import OrderSummary from "@/components/MyCart/OrderSummary.vue";
import OrderCardV2 from "@/components/MyCart/OrderCardV2.vue";
import OrderCardV3 from "@/components/MyCart/OrderCardV3.vue";
import EmptyCart from "@/components/MyCart/EmptyCart.vue";
import { mapCartToGAEvent } from "@/plugins/gtag.client";

const { $toast } = useNuxtApp();
const { t } = useI18n();
const router = useRouter();

const { platformId } = storeToRefs(usePlatformStore());

const gtag = useGtag();

const { getCartData } = useCartStore();
const { numOfProducts, numOfSamples, cartSamples, cartProductsGroupByWarehouse, cart } = storeToRefs(useCartStore());

const isCartDataReady = ref(false);
if (!cart.value) {
	try {
		const result = await getCartData();
		if (result === "Success") {
			isCartDataReady.value = true;
		}
	} catch (error) {
		// To auth
		router.push("/auth");
	}
} else {
	isCartDataReady.value = true;
}

if (isCartDataReady.value && cartProductsGroupByWarehouse.value && cartSamples.value) {
	const eventData = mapCartToGAEvent(cartProductsGroupByWarehouse.value, cartSamples.value);
	gtag.event("view_cart", eventData);
}

const goShopping = () => {
	router.push("/textiles");
};

// a flag for order summary
// 		true: this order has both products and samples
// 		false: this order has only one of them
const hasBoth = computed(() => {
	if (numOfProducts.value > 0 && numOfSamples.value > 0) {
		return true;
	} else {
		return false;
	}
});

const { createCheckoutAuthorizationAuth } = useURMApi();
const { checkoutUrl } = useRuntimeConfig().public;

const productsAndSamples = computed(() => {
	const products = `${numOfProducts.value} ${t("general.products", { count: numOfProducts.value })}`;
	const samples =
		numOfSamples.value > 0 ? `, ${numOfSamples.value} ${t("general.samples", { count: numOfSamples.value })}` : "";
	return `(${products}${samples})`;
});

async function goToCheckout() {
	// no samples or number of smaples >= 5 can go to checkout
	if (numOfSamples.value >= 5 || numOfSamples.value === 0) {
		// 1. fetch Checkout auth token
		const { response } = await createCheckoutAuthorizationAuth({
			platform_id: platformId.value,
			cart_id: cart.value?.[0]?.id ?? (cart.value as any).id,
		});
		const checkoutToken = response.value?.data.checkout_token;
		// 2. if has auth token, redirect to checkout page
		const fromUrl = window.location.host;
		if (checkoutToken) {
			const langCode = ref<string>("en");
			const cached = localStorage.getItem("lang");
			if (cached) {
				langCode.value = JSON.parse(cached).code;
			}
			sessionStorage.clear();
			window.location.href = `${checkoutUrl}?token=${checkoutToken}&platformId=${platformId.value}&fromUrl=${fromUrl}&lang=${langCode.value}`;
		}
	} else {
		($toast as Toast)({
			message: t("pages.cart.a_minimum_of_5_samples"),
			type: "warning",
			location: "top right",
			closable: true,
			duration: 10000,
		});
	}
}
</script>
<style lang="scss" scoped>
.cart_container {
	@apply flex h-full max-w-[1360px] mx-auto;
	@apply mobile:flex-col;

	.content_wrapper {
		@apply pr-[40px] mb-[48px] mt-[80px] mobile:mt-[70px] mobile:mb-0;
		@apply w-[70%] mobile:w-full;

		.content {
			// @apply tablet:px-[4%];
			@apply mobile:px-0 mobile:w-full;

			.title {
				@apply text-[40px] leading-[48px] font-dm-serif  mb-[24px];
				@screen mobile {
					@apply text-[24px] leading-[40px] px-[15px];
				}

				.quantity {
					@apply text-[20px] leading-[30px] text-neutral-700;
				}

				.total_button {
					@apply text-[14px] leading-[20px] text-neutral-900 font-lato hidden mobile:block;

					.total {
						@apply flex justify-between items-center;
						.total_price {
							@apply text-[20px] leading-[30px] font-bold;
						}

						.dollar {
							@apply text-[14px] font-normal ml-[2px];
						}
					}
				}
			}

			.alert_container {
				@screen mobile {
					@apply px-[15px];
				}

				.alert {
					@apply border-l-[4px];
				}
			}

			.cart {
				@apply mt-[16px] flex items-start space-x-[40px] mobile:flex-col;

				.order_cards {
					@apply w-full;
				}

				.continue_shopping {
					@apply flex flex-row justify-start text-[14px] leading-[20px];
					@apply mt-[32px] cursor-pointer;
				}
			}
		}
	}
}

.v-alert {
	@apply mobile:h-auto;
}
</style>
